import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { IconButton } from 'shared/ui/IconButton'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { useWidgetKeyboard } from 'pages/chatbot/ui/UnifiedAgent/hooks/useWidgetKeyboard'
import { getLastStepHintMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/getLastStepHintMessage'
import {
  WidgetCard,
  WidgetFooter,
  WidgetHint,
} from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import {
  InstructionsWidgetStore,
  STEP_HEADERS,
} from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsWidgetStore'
import { InstructionsStep1 } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/InstructionsStep1'
import { InstructionsStep2 } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/InstructionsStep2'
import { InstructionsStep3 } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/InstructionsStep3'

const DEFAULT_HINT = ['↑↓ to navigate', 'Enter to select', 'Esc to skip']

export const InstructionsWidget = observer(() => {
  const { instructionsSectionStore, chatStore, entities } = useUnifiedAgentContext()
  const [store] = useState(
    () => new InstructionsWidgetStore(instructionsSectionStore, chatStore, entities)
  )

  useEffect(() => {
    return () => {
      store.reset()
    }
  }, [])

  useWidgetKeyboard({
    onNavigate: store.step < 3 ? (dir) => store.moveFocus(dir) : undefined,
    onEnter: store.step < 3 ? store.handleSelect : undefined,
    onNext: store.step === 2 && !store.isNextDisabled ? store.advanceStep : undefined,
    onSubmit: store.isLastStep && !store.isNextDisabled ? store.handleSubmit : undefined,
    onEscape: store.handleClose,
    isLastStep: store.isLastStep,
  })

  const hintItems = () => {
    if (store.step === 2) {
      return getLastStepHintMessage(['↑↓ to navigate', 'Enter to select'])
    }
    return store.isLastStep ? getLastStepHintMessage([]) : DEFAULT_HINT
  }

  const renderContent = () => {
    switch (store.step) {
      case 1:
        return <InstructionsStep1 focusedIndex={store.safeIndex} onSelect={store.selectAt} />
      case 2:
        return (
          <InstructionsStep2
            items={store.filteredFields}
            selectedKeys={store.selectedFieldKeys}
            focusedIndex={store.safeIndex}
            search={store.search}
            onSearch={store.setSearch}
            onToggle={store.toggleField}
            onFocus={store.setFocusedIndex}
            onReconnect={store.reconnect}
          />
        )
      case 3:
        return (
          <InstructionsStep3
            selectedFields={store.selectedFields}
            promptText={store.promptText}
            onPromptChange={store.setPromptText}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <WidgetCard header={STEP_HEADERS[store.step - 1]}>
        {renderContent()}
        <WidgetFooter
          step={store.step}
          total={store.totalSteps}
          onSkip={store.handleClose}
          onBack={store.step > 1 ? store.goBack : undefined}
          action={
            store.step === 2 ? (
              <IconButton
                icon={'arrowRight'}
                ariaLabel={'WidgetNext'}
                color={'primary'}
                onClick={store.advanceStep}
                disabled={store.isNextDisabled}
              />
            ) : store.isLastStep ? (
              <IconButton
                icon={'arrowUp'}
                ariaLabel={'WidgetSubmit'}
                color={'primary'}
                onClick={store.handleSubmit}
                disabled={store.isNextDisabled}
              />
            ) : null
          }
        />
      </WidgetCard>
      <WidgetHint items={hintItems()} />
    </>
  )
})
